import * as ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-xcode';
import StringUtils from './StringUtils';

export default class FormUtils {
	static select2AjaxDelay = 200;

	static _invalid_tooltip_handlers_bound = false;

	static _switchery_visibility_handlers_bound = false;

	static _switchery_intersection_observer = null;

	static _switchery_disabled_handling_patched = false;

	/**
	 * Switchery 0.1.x only checks disabled when binding click. Patch so each click re-checks
	 * input.disabled / options.disabled (needed for dynamic lock/unlock after init).
	 *
	 * @param {{ prototype?: { handleClick?: Function } }} SwitcheryCtor
	 */
	static patchSwitcheryDisabledHandling(SwitcheryCtor) {
		if (FormUtils._switchery_disabled_handling_patched || !SwitcheryCtor?.prototype) {
			return;
		}

		FormUtils._switchery_disabled_handling_patched = true;

		SwitcheryCtor.prototype.handleClick = function handleClickRespectingDisabled() {
			const instance = this;
			const switcher = this.switcher;

			const onClick = () => {
				if (instance.isDisabled()) {
					return;
				}

				instance.setPosition(true);
			};

			if (switcher.addEventListener) {
				switcher.addEventListener('click', onClick);
			} else {
				switcher.attachEvent('onclick', onClick);
			}
		};
	}

	/**
	 *
	 * @param {string} selector
	 * @param {boolean} visible
	 */
	static toggleFieldVisibility(selector, visible, customHiddenClass = 'hidden') {
		const field = typeof selector === 'string' ? $(selector) : selector;
		const container = field.closest('.field-container');
		container.toggleClass(customHiddenClass, !visible);
		if (field.prop('tagName') === 'DIV') {
			field.find('input, textarea, select').each(function () {
				FormUtils.toggleFieldEnabled($(this), visible);
			});
		} else {
			FormUtils.toggleFieldEnabled(selector, visible);
		}
	}

	/**
	 * Recalculate Switchery knob position after its container becomes visible.
	 * Switchery measures width at init; hidden elements report 0 and render misaligned.
	 *
	 * @param {Document|HTMLElement|JQuery|string|null} scope
	 */
	static refreshSwitcheryPositions(scope = document) {
		const $root = scope instanceof jQuery ? scope : $(scope ?? document);

		$root.find('input[data-plugin="switchery"]').each(function () {
			const input = this;
			// Switchery appendChild()s its <span class="switchery"> to the input's parent, so with the
			// markup `<input><label><span class="switchery">` it is NOT input.nextElementSibling (that's
			// the label) — it's the last switchery element in the same container.
			const switcher = input.parentElement?.querySelector('.switchery');

			if (!switcher) {
				return;
			}

			const jack = switcher.querySelector('small');

			if (!jack) {
				return;
			}

			if (input.checked) {
				const switcher_width = parseInt(window.getComputedStyle(switcher).width, 10);
				const jack_width = jack.offsetWidth;

				// A switch that is still display:none (hidden tab/field, or an iframe in a collapsed
				// panel) reports a 0px-wide knob. Positioning it now would place the knob at the far
				// right edge (switcher_width - 0), which is exactly the "detached knob" glitch. Skip it
				// and let a later refresh — fired once the switch is actually laid out — position it.
				if (!switcher_width || !jack_width) {
					return;
				}

				jack.style.left = `${Math.max(0, switcher_width - jack_width)}px`;
			} else {
				jack.style.left = '0';
			}
		});
	}

	/**
	 * Reposition each Switchery knob the moment its element actually becomes visible — regardless of
	 * HOW it was revealed (tab switch, superadmin editing-mode CSS, or the translation iframe's panel
	 * un-collapsing). Switchery measures the knob width once at init; anything created while hidden
	 * reports 0px and renders with a detached knob. IntersectionObserver is the only reveal signal that
	 * fires for the CSS-driven editing-mode path too (the container keeps its `hidden` class, so a
	 * class MutationObserver never sees it).
	 *
	 * @param {Document|HTMLElement|JQuery|string|null} scope
	 */
	static observeSwitcheryVisibility(scope = document) {
		if (typeof IntersectionObserver === 'undefined') {
			return;
		}

		if (!FormUtils._switchery_intersection_observer) {
			FormUtils._switchery_intersection_observer = new IntersectionObserver((entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						FormUtils.scheduleSwitcheryPositionRefresh();
					}
				}
			});
		}

		const observer = FormUtils._switchery_intersection_observer;
		const $root = scope instanceof jQuery ? scope : $(scope ?? document);

		$root.find('input[data-plugin="switchery"]').each(function () {
			const target = this.parentElement?.querySelector('.switchery') ?? this.closest('.switch-container');
			if (target) {
				observer.observe(target);
			}
		});
	}

	/**
	 * @param {Document|HTMLElement|JQuery|string|null} scope
	 */
	static scheduleSwitcheryPositionRefresh(scope = document) {
		requestAnimationFrame(() => FormUtils.refreshSwitcheryPositions(scope));
	}

	/**
	 * Force a Switchery-backed checkbox to match `checked` (jack position + on/off colors).
	 * Clicks the Switchery span when the state differs so Switchery's own setPosition(true) runs.
	 *
	 * @param {HTMLInputElement|null|undefined} input
	 * @param {boolean} checked
	 */
	static syncSwitcheryChecked(input, checked) {
		if (!(input instanceof HTMLInputElement)) {
			return;
		}

		const desired = Boolean(checked);

		if (input.checked === desired) {
			FormUtils.refreshSwitcheryPositions(input.parentElement ?? document);

			return;
		}

		const switcher = input.parentElement?.querySelector('.switchery');

		if (switcher instanceof HTMLElement) {
			switcher.click();

			return;
		}

		input.checked = desired;
		FormUtils.refreshSwitcheryPositions(input.parentElement ?? document);
	}

	/**
	 * Refresh Switchery controls when a field container becomes visible or layout changes.
	 * Covers tab switches, dynamic show/hide, iframe resize, and popup handshakes.
	 */
	static bindSwitcheryVisibilityRefresh() {
		if (FormUtils._switchery_visibility_handlers_bound) {
			return;
		}

		FormUtils._switchery_visibility_handlers_bound = true;

		const maybeRefreshContainer = (element) => {
			if (!(element instanceof Element)) {
				return;
			}

			const container = element.classList.contains('field-container')
				? element
				: element.closest('.field-container');

			if (!container || !container.querySelector('input[data-plugin="switchery"]')) {
				return;
			}

			if (container.classList.contains('hidden') || container.classList.contains('user-disabled')) {
				return;
			}

			FormUtils.scheduleSwitcheryPositionRefresh(container);
		};

		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') {
					continue;
				}

				maybeRefreshContainer(mutation.target);
			}
		});

		const observe_root = document.getElementById('pageForm') ?? document.body;

		observer.observe(observe_root, {
			subtree: true,
			attributes: true,
			attributeFilter: ['class'],
		});

		let resize_timeout;

		window.addEventListener('resize', () => {
			clearTimeout(resize_timeout);
			resize_timeout = setTimeout(() => FormUtils.refreshSwitcheryPositions(), 150);
		});

		window.addEventListener('handshakeDone', () => {
			FormUtils.scheduleSwitcheryPositionRefresh();
		});
	}

	static toggleFieldEnabled(selector, enabled) {
		const field = typeof selector === 'string' ? $(selector) : selector;
		// Prefer attr over .data(): jQuery caches data-* and ignore later removeAttribute().
		const readonly = field.attr('data-readonly') === 'true' || field.prop('readonly') === true;
		let disabled = !enabled;

		if (readonly) {
			disabled = true;
		}

		field.prop('disabled', disabled);

		// Switchery ignores disabled after init; keep the visual control in sync with the input.
		if (field.is('input[data-plugin="switchery"]')) {
			FormUtils.syncSwitcheryInteraction(field.get(0), !disabled);
		}

		const container = field.closest('.field-container');
		container.find('.additional-buttons button').prop('disabled', disabled);
		container.find('.additional-buttons a').css({
			'pointer-events': enabled && !disabled ? 'auto' : 'none',
			cursor: enabled && !disabled ? 'pointer' : 'not-allowed',
		});
	}

	/**
	 * Enable/disable Switchery UI interaction for an input (library ignores disabled after init).
	 * CSS also blocks :disabled ~ .switchery; this covers edge cases (label / stale styles).
	 *
	 * @param {HTMLInputElement|null|undefined} input
	 * @param {boolean} interactive
	 */
	static syncSwitcheryInteraction(input, interactive) {
		if (!(input instanceof HTMLInputElement)) {
			return;
		}

		const switch_container = input.closest('.switch-container');
		const switcher = input.parentElement?.querySelector('.switchery');

		if (switch_container instanceof HTMLElement) {
			switch_container.style.pointerEvents = interactive ? '' : 'none';
			switch_container.style.cursor = interactive ? '' : 'not-allowed';
		}

		if (switcher instanceof HTMLElement) {
			switcher.style.pointerEvents = interactive ? '' : 'none';
			switcher.style.cursor = interactive ? '' : 'not-allowed';
		}
	}

	/**
	 * Lock/unlock a Switchery field: optional checked sync, data-readonly, disabled + UI block.
	 *
	 * @param {HTMLInputElement|JQuery|string} input
	 * @param {boolean} locked
	 * @param {{ checked?: boolean }} [options]
	 */
	static setSwitcheryLocked(input, locked, options = {}) {
		const field = typeof input === 'string' ? $(input) : input instanceof HTMLElement ? $(input) : input;
		const el = field.get(0);

		if (!(el instanceof HTMLInputElement)) {
			return;
		}

		if (locked) {
			if (Object.prototype.hasOwnProperty.call(options, 'checked')) {
				FormUtils.syncSwitcheryChecked(el, Boolean(options.checked));
			}

			field.attr('data-readonly', 'true');
			FormUtils.toggleFieldEnabled(field, false);

			return;
		}

		field.removeAttr('data-readonly');
		field.removeData('readonly');

		FormUtils.toggleFieldEnabled(field, true);
	}

	/**
	 * Ensures readonly/disabled controls are included in the next serialize/submit (hidden inputs + release disabled).
	 *
	 * @param {string|JQuery} $form
	 */
	static prepareReadonlyFieldsForSubmit($form = '#pageForm') {
		if (typeof $form === 'string') {
			$form = $($form);
		}

		$form.find('textarea[data-plugin="summernote"][data-readonly="true"]').each(function () {
			const $textarea = $(this);
			const field_name = $textarea.attr('id');

			if (!field_name) {
				return;
			}

			let content = $textarea.val();

			if ($textarea.next('.note-editor').length > 0) {
				content = $textarea.summernote('code');
				$textarea.val(content);
			}

			$form.find(`input[type="hidden"][name="${field_name}"]`).val(content);
		});

		$form.find('select[data-readonly="true"]:disabled').each(function () {
			const $select = $(this);
			const field_name = $select.attr('name') || $select.attr('id');

			if (!field_name) {
				return;
			}

			let selected_value = $select.val();

			if (selected_value === null || selected_value === undefined || selected_value === '') {
				const original_value = $select.attr('original-value');

				if (original_value !== undefined && original_value !== null && original_value !== '') {
					selected_value = original_value;
				}
			}

			if (
				selected_value === null ||
				selected_value === undefined ||
				selected_value === '' ||
				selected_value === 'null' ||
				selected_value === 'undefined'
			) {
				return;
			}

			const hidden_name = field_name.replace(/\[\]$/, '');
			let $hidden = $form.find(`input[type="hidden"][name="${hidden_name}"], input[type="hidden"][name="${hidden_name}[]"]`).first();

			if (!$hidden.length) {
				$hidden = $('<input type="hidden">').attr('name', hidden_name).appendTo($form);
			}

			if (Array.isArray(selected_value)) {
				$hidden.val(selected_value[0] ?? '');
			} else {
				$hidden.val(selected_value);
			}
		});

		$form.find('[data-readonly="true"]:disabled').each(function () {
			const $field = $(this);

			if ($field.is('select') || $field.is('input[type="checkbox"]') || $field.is('input[type="radio"]')) {
				return;
			}

			$field.data('readonly-released-for-submit', true);
			$field.prop('disabled', false);
		});
	}

	static getFormData($form = '#pageForm') {
		if (typeof $form === 'string') {
			$form = $($form);
		}

		FormUtils.prepareReadonlyFieldsForSubmit($form);

		const unindexed_array = $form.serializeArray();
		const indexed_array = {};

		$.map(unindexed_array, function (n, i) {
			const $field = $form.find(`[name="${n['name']}"]`);
			if ($field.length && $field[0].nodeName === 'SELECT') {
				indexed_array[n['name']] = $field.val();
			} else {
				indexed_array[n['name']] = n['value'];
			}
		});

		return indexed_array;
	}

	static shortenPath(text, max_length = 30) {
		if (typeof text !== 'string') {
			return text;
		}

		// Option text carries the Blade indentation; an empty root name leaves a leading ">".
		const trimmed = text.trim();
		const segments = trimmed.split('>').map((segment) => segment.trim()).filter((segment) => segment !== '');

		if (trimmed.length <= max_length || segments.length < 3) {
			return trimmed;
		}

		const prefix = trimmed.startsWith('>') ? '> ' : '';

		return prefix + [segments[0], '…', segments[segments.length - 1]].join(' > ');
	}

	/**
	 * Out-of-ACL associations stay selected but must not be removable in Select2.
	 *
	 * @param {{ element?: Element|null }|null|undefined} data
	 * @returns {boolean}
	 */
	static isAclLockedSelect2Data(data) {
		const element = data?.element;
		if (!element) {
			return false;
		}

		return (
			element.getAttribute('data-acl-locked') === 'true' ||
			element.disabled === true
		);
	}

	/**
	 * Replace the Select2 remove control with a closed padlock (same chip colors).
	 *
	 * @param {JQuery|HTMLElement} choice
	 */
	static applyAclLockedChoiceUi(choice) {
		const $choice = $(choice);
		if (!$choice.length) {
			return;
		}

		$choice.addClass('acl-locked-choice');

		const $remove = $choice.find('.select2-selection__choice__remove').first();
		if (!$remove.length || $remove.data('aclLockApplied')) {
			return;
		}

		$remove
			.data('aclLockApplied', true)
			.attr({
				title: 'Non rimovibile (fuori dal perimetro ACL)',
				'aria-label': 'Non rimovibile (fuori dal perimetro ACL)',
				tabindex: '-1',
			})
			.html('<i class="fa-solid fa-lock" aria-hidden="true"></i>');
	}

	static #markAclLockedChoice(data, container) {
		if (!container || !FormUtils.isAclLockedSelect2Data(data)) {
			return;
		}

		// Select2 adds the remove control after templateSelection returns.
		window.requestAnimationFrame(() => {
			FormUtils.applyAclLockedChoiceUi(container);
		});
	}

	static #templateSelectionCallback(multiple, selector) {
		return (data, container) => {
			FormUtils.#markAclLockedChoice(data, container);

			if (multiple) {
				const length = $(selector).val().length;
				if (length > 1) {
					return length + ' selezionati';
				}
			}

			return data.text;
		};
	}

	static initializeAceEditor(selector, textarea, mode = 'json') {
		if (typeof textarea === 'string') {
			textarea = $(textarea);
		}

		// Mark source field so Form.formChangeHandler includes it when hidden (Ace replaces visible editing).
		textarea.attr('data-ace-editor-sync', '1');

		const editor = ace.edit(selector);
		editor.setTheme('ace/theme/xcode');
		editor.getSession().setMode(`ace/mode/${mode}`);
		editor.getSession().setOptions({
			tabSize: 4,
			useSoftTabs: true,
			useWorker: false,
		});

		let ace_initializing = true;

		editor.getSession().on('change', function () {
			if (ace_initializing) {
				return;
			}

			textarea.val(editor.getSession().getValue());

			if (window.form?.formChangeHandler) {
				window.form.formChangeHandler({ type: 'change' }, false);
			} else {
				textarea.trigger('change');
			}
		});

		editor.setShowPrintMargin(false);

		textarea.hide();

		const seed_value = textarea.val() || editor.getSession().getValue();
		editor.getSession().setValue(seed_value, -1);
		textarea.val(editor.getSession().getValue());
		ace_initializing = false;

		return editor;
	}

	/**
	 *
	 * @param {string} selector element selector
	 * @param {boolean} multiple is a multiple select?
	 * @param {string|number|'auto'} width width of the select2
	 * @param {string|null} placeholder placeholder of the select2 when no selection
	 * @param {boolean} truncateMultipleSelections truncate multiple selections and display the number of selected items
	 * @param {Object} customConfigs custom configurations for the select2
	 *
	 * @returns {jQuery} the select2 element
	 */
	static initializeSelect2(
		selector,
		multiple = false,
		width = 'auto',
		dropdownAutoWidth = true,
		placeholder = null,
		truncateMultipleSelections = false,
		customConfigs = {},
	) {
		const config = {
			width,
			multiple,
			closeOnSelect: customConfigs.closeOnSelect || !multiple,
			dropdownAutoWidth,
			placeholder: placeholder || 'Tutti',
			allowClear: customConfigs.allowClear || true,
			...customConfigs,
		};

		if (truncateMultipleSelections) {
			config.templateSelection = FormUtils.#templateSelectionCallback(multiple, selector);
		}

		if (config.ajax) {
			// config.ajax.cache = true;
			config.ajax.url = StringUtils.localizeUrl(config.ajax.url);

			if (!config.ajax.data) {
				config.ajax.data = function (params) {
					return {
						q: params.term,
						page: params.page || 1,
						selected: $(selector).val(),
					};
				};
			}

			if (!config.ajax.processResults) {
				config.ajax.processResults = function (data) {
					return {
						results: $.map(FormUtils.resolveSelect2AjaxItems(data), function (obj) {
							return FormUtils.mapSelect2AjaxOption(obj);
						}),
						pagination: data.pagination || null,
					};
				};
			}

			if (!config.ajax.dataType) {
				config.ajax.dataType = 'json';
			}

			config.ajax.delay = FormUtils.select2AjaxDelay;
		}

		if (!config.escapeMarkup) {
			config.escapeMarkup = (markup) => markup;
		}

		if (!config.templateResult) {
			config.templateResult = (data) => {
				if (data.loading) {
					return data.text;
				}
				if (data.html) {
					return data.html;
				}

				const optionElement = data.element;
				if (!optionElement || data.id === undefined || data.id === null || data.id === '') {
					return data.text;
				}

				const localeCode = optionElement.getAttribute('data-locale');
				const flagCode = optionElement.getAttribute('data-flag') || localeCode;
				const rawTranslations = optionElement.getAttribute('data-translations');
				let translations = null;

				if (rawTranslations) {
					try {
						const parsedTranslations = JSON.parse(rawTranslations);
						if (Array.isArray(parsedTranslations)) {
							translations = parsedTranslations;
						}
					} catch (error) {
						translations = null;
					}
				}

				return FormUtils.drawSelect2OptionWithThumbs(data.text, false, data.id, flagCode, localeCode, translations);
			};
		}

		if (!config.templateSelection) {
			config.templateSelection = (data, container) => {
				FormUtils.#markAclLockedChoice(data, container);

				const is_hidden =
					data.visibility === false ||
					(data.element && ($(data.element).attr('data-visibility') === 'false' || $(data.element).data('visibility') === false));

				// data.text stays whole: Select2 uses it as the chip title, so hover shows the full path.
				const text = FormUtils.shortenPath(data.text);

				if (is_hidden) {
					return `${text} <i class="fa-regular fa-eye-slash text-muted float-right ml-1" aria-hidden="true"></i>`;
				}

				return text;
			};
		}

		const $selector = $(selector).select2(config);

		// ------------------------------------------------------

		// Set flag on mousedown (before Select2 handles click) so select2:opening can be prevented
		const $container = $selector.data('select2')?.$container;
		if ($container?.length) {
			$container.on('mousedown', '.select2-selection__choice__remove', function (event) {
				const $choice = $(this).closest('.select2-selection__choice');
				if ($choice.hasClass('acl-locked-choice')) {
					event.preventDefault();
					event.stopPropagation();

					return false;
				}

				$selector.data('preventNextOpen', true);
			});
		}

		$selector
			.on('select2:opening', function (evt) {
				if ($(this).data('preventNextOpen')) {
					$(this).removeData('preventNextOpen');
					evt.preventDefault();
				}
			})
			.on('select2:unselecting', function (evt) {
				const data = evt.params?.args?.data;
				if (FormUtils.isAclLockedSelect2Data(data)) {
					evt.preventDefault();
				}
			})
			.on('select2:select', function (evt) {
				const $this = $(this);
				$this.parent().find('.select2-search__field').val('');

				FormUtils.handleSelect2Change(evt, $this);
			})
			.on('select2:unselect', function (evt) {
				if (evt.params.originalEvent) {
					const $this = $(this);

					const values = $this.val();
					if (Array.isArray(values)) {
						$this.val(values.filter((item) => item !== evt.params.data.id.toString())).trigger('change');
					}

					const choiceId = evt.params.originalEvent.currentTarget.getAttribute('data-select2-id');
					$(`#select2-${evt.currentTarget.id}-results .select2-results__option[data-select2-id="${choiceId}"]`)
						.removeClass('select2-results__option--selected')
						.attr('aria-selected', 'false');

					// 	// Non rimuovere le opzioni per i select multipli con AJAX
					// 	// perché Select2 potrebbe cercare di ripristinarle durante le richieste AJAX
					// 	// causando conflitti di selezione/deselezione
					// 	const $select = $(evt.currentTarget);
					// 	if (!$select.prop('multiple')) {
					// 		$('#' + evt.currentTarget.id + " option[value='" + evt.params.data.id + "']").remove();
				}

				FormUtils.handleSelect2Change(evt, $(this));
			});

		setTimeout(() => {
			FormUtils.handleSelect2Change(null, $selector);
			if ($selector.closest('#pageForm').length) {
				window.form?.scheduleFinalizeInitialFormState?.(200);
			}
		}, 300);

		// ------------------------------------------------------

		return $selector;
	}

	/**
	 * Attach select2 to every `<x-forms.creatable-select>` in scope: a fixed list of values
	 * the editor can add to on the fly, so a value missing from the list does not mean
	 * leaving the form.
	 *
	 * A term already present in the list is NOT offered as new — the match is trimmed and
	 * case-insensitive. That is the point of the component: on the agency category mapping a
	 * stray "economia" typed next to "Economia, affari e finanza" becomes a second, permanent
	 * category downstream, and nothing merges the two back.
	 *
	 * @param {string|jQuery|HTMLElement} scope root to look into, the whole page by default
	 *
	 * @returns {jQuery} the initialized selects
	 */
	static initializeCreatableSelects(scope = document) {
		const $selects = $(scope).find('select[data-creatable="true"]').addBack('select[data-creatable="true"]');

		$selects.each(function () {
			const $select = $(this);
			const create_label = $select.attr('data-create-label') || 'Crea';
			const max_length = parseInt($select.attr('data-max-length'), 10) || 0;

			FormUtils.initializeSelect2($select, $select.prop('multiple'), '100%', false, $select.attr('data-placeholder') || null, false, {
				tags: true,
				allowClear: $select.attr('data-allow-clear') !== 'false',
				createTag(params) {
					const term = $.trim(params.term);

					if (term === '' || (max_length > 0 && term.length > max_length)) {
						return null;
					}

					const already_listed = $select
						.find('option')
						.toArray()
						.some((option) => option.value.trim().toLowerCase() === term.toLowerCase());

					return already_listed ? null : { id: term, text: term, newTag: true };
				},
				insertTag(data, tag) {
					// Bottom of the list, so the existing values stay the first thing read.
					data.push(tag);
				},
				templateResult(data) {
					if (data.loading) {
						return data.text;
					}

					if (data.newTag) {
						return `<span class="text-primary"><i class="fa-regular fa-plus fa-fw mr-1"></i>${create_label} "${FormUtils.escapeHTML(
							data.text,
						)}"</span>`;
					}

					return FormUtils.escapeHTML(data.text);
				},
				templateSelection(data) {
					return FormUtils.escapeHTML(data.text);
				},
			});
		});

		return $selects;
	}

	static handleSelect2Change(evt, element) {
		if (element.closest('form').is('#filtersForm')) {
			const label = element.siblings('h6');
			const container = label.siblings('.select2-container').find('.select2-selection');
			let currentValue = element.select2('val');
			if (evt?.params._type === 'unselect') {
				const id = evt.params.data.id.toString();
				currentValue = Array.isArray(currentValue) ? currentValue.filter((item) => item !== id) : null;
			}
			if (
				currentValue === null ||
				currentValue === '' ||
				currentValue === 'all' ||
				currentValue === '0' ||
				(Array.isArray(currentValue) && currentValue.length === 0)
			) {
				container.removeClass('border border-primary');
			} else if (label.find('i.fa-circle').length === 0) {
				container.addClass('border border-primary');
			}
		}
	}

	static escapeHTML(str) {
		var p = document.createElement('p');
		p.appendChild(document.createTextNode(str));
		return p.innerHTML;
	}

	static normalizeBaseLocale(locale) {
		if (!locale || typeof locale !== 'string') {
			return '';
		}

		const normalized = locale.toLowerCase().replace(/-/g, '_');

		return normalized.split('_')[0] || normalized;
	}

	static drawSelect2OptionWithThumbs(text, thumbUrl = null, id = null, flagCode = null, localeCode = null, translations = null, visibility = undefined) {
		const normalizedFlagCode = flagCode ? String(flagCode).toLowerCase().trim() : null;
		const normalizedLocaleCode = localeCode ? String(localeCode).toLowerCase().trim() : '';
		const hasFlag = normalizedFlagCode && /^[a-z0-9_-]+$/.test(normalizedFlagCode);
		const flagTitle = normalizedLocaleCode ? ` title="${FormUtils.escapeHTML(normalizedLocaleCode.toUpperCase())}"` : '';

		// When the option carries a translations family, only show flags for languages other than the
		// default one: the default-language flag is implicit and would otherwise be shown for every item.
		const hasTranslationData = Array.isArray(translations);
		const defaultLocale = FormUtils.normalizeBaseLocale(typeof window !== 'undefined' ? window.default_locale : '');
		const normalizedTranslationFlags = hasTranslationData
			? [
					...new Set(
						translations
							.filter((translation) => FormUtils.normalizeBaseLocale(translation?.locale) !== defaultLocale)
							.map((translation) => translation?.flag || translation?.locale || null)
							.filter((value) => typeof value === 'string' && value.length > 0)
							.map((value) => value.toLowerCase().trim())
							.filter((value) => /^[a-z0-9_-]+$/.test(value)),
					),
			  ]
			: [];
		const translatedFlagsHtml = normalizedTranslationFlags
			.map(
				(code) =>
					`<img src="/images/flags/${code}.png" class="item-select-flag" alt="" style="height: 14px; width: auto;" onerror="this.style.display='none'" />`,
			)
			.join('');
		// Fall back to the single primary flag only for non-translatable options (no translations family):
		// translatable options already convey their languages through the (default-excluded) flags above.
		const flagHtml = translatedFlagsHtml
			? `<span class="content-translations d-flex flex-nowrap gap-1 align-items-center justify-content-end item-select-flag-wrapper">${translatedFlagsHtml}</span>`
			: !hasTranslationData && hasFlag
			? `<span class="content-translations d-flex flex-nowrap gap-1 align-items-center justify-content-end item-select-flag-wrapper"><img src="/images/flags/${normalizedFlagCode}.png" class="item-select-flag" alt=""${flagTitle} style="height: 14px; width: auto;" onerror="this.parentNode.style.display='none'" /></span>`
			: '';

		// Hidden options (visibility === false) get a muted style + eye-slash icon (merged from dev).
		const visibility_icon =
			visibility === false
				? ' <i class="fa-regular fa-eye-slash text-muted float-right ml-1" aria-hidden="true"></i>'
				: '';
		const text_muted_class = visibility === false ? ' text-muted' : '';

		const thumbHtml =
			thumbUrl === false
				? ''
				: thumbUrl
				? `<img src="${thumbUrl}" class="item-select-thumb" alt="" onerror="this.style.display='none'" />`
				: `<span class="item-select-thumb bg-grey-200 d-inline-flex justify-content-center align-items-center grey-300">
					<i class="icon fa-solid fa-image fa-fw"></i>
				</span>`;

		return `${thumbHtml}<span class="item-select-text flex-fill${text_muted_class}" data-option-id="${id}">${text}</span>${flagHtml}${visibility_icon}`;
	}

	static mapSelect2AjaxOption(obj) {
		const thumbUrl = obj.cover && !obj.cover.nocover ? obj.cover.thumb || obj.cover.original : obj.cover ? null : false;

		let text = obj.path || obj.name || obj.title || obj.text || obj;
		if (obj.published_at && obj.published_at.human) {
			if (!text.startsWith(obj.published_at.human)) {
				text = obj.published_at.human + ' - ' + text;
			}
		}

		const categorization = obj.sections || obj.folders || obj.categories || obj.types || obj.tags;
		if (categorization && categorization.length > 0) {
			text += ' [' + categorization.map((item) => item.name || item.title || item).join(', ') + ']';
		}

		const localeCode = obj.locale || obj.language?.code || obj.lang || null;
		const flagCode = obj.flag || obj.language?.flag || localeCode;

		return {
			id: obj.id || obj,
			url: StringUtils.localizeUrl(obj.URL || obj.href || null),
			text: FormUtils.escapeHTML(text),
			thumb: thumbUrl,
			visibility: obj.visibility,
			html: FormUtils.drawSelect2OptionWithThumbs(text, thumbUrl, obj.id || null, flagCode, localeCode, obj.translations || null, obj.visibility),
		};
	}

	static resolveSelect2AjaxItems(data) {
		if (Array.isArray(data)) {
			return data;
		}

		if (Array.isArray(data?.results)) {
			return data.results;
		}

		if (Array.isArray(data?.data)) {
			return data.data;
		}

		return [];
	}

	static openFormFieldConfigurator(fieldConfigs) {
		if (fieldConfigs.type === 'select') {
			fieldConfigs.attrs = Object.assign({ multiple: false, 'data-close-on-select': true }, fieldConfigs.attrs);
		} else if (fieldConfigs.type === 'number') {
			fieldConfigs.attrs = Object.assign({ min: 1, step: 1 }, fieldConfigs.attrs);
		} else if (fieldConfigs.type === 'text') {
			fieldConfigs.attrs = Object.assign({ 'data-seo-max': null, 'data-seo-min': null }, fieldConfigs.attrs);
		}

		let saveButton = null;

		$.magnificPopup.open(
			{
				items: {
					src: $('#popup'),
				},
				type: 'inline',
				closeOnBgClick: false,
				callbacks: {
					close: function () {
						saveButton.off('click');
						saveButton = null;
						const container = $('#popup-content');
						container.empty();
					},
					open: function () {
						const container = $('#popup-content');
						container.empty();

						const form = FormUtils.#getFormFieldHtml(fieldConfigs);
						container.html(form);

						let fleditor = ace.edit('config_attributes');
						let textarea = $('#config_attributes').siblings('textarea').first();

						fleditor.setTheme('ace/theme/github');
						fleditor.getSession().setMode('ace/mode/json');
						fleditor.getSession().setOptions({
							// theme: 'ace/theme/github',
							// mode: 'ace/mode/json',
							tabSize: 4,
							useSoftTabs: true,
						});
						fleditor.getSession().on('change', function () {
							textarea.val(fleditor.getSession().getValue());
						});
						const stringified = Array.isArray(fieldConfigs.attrs) ? '{}' : JSON.stringify(fieldConfigs.attrs, null, '\t');
						fleditor.setValue(stringified);
						textarea.val(stringified);

						saveButton = $('#editor-configs-form-submit');
						saveButton.off('click').on('click', function () {
							let attrs = {};
							try {
								attrs = JSON.parse($('#config_attributes').siblings('textarea').first().val() || '{}');
							} catch (e) {
								toastr.error('Formato JSON degli attributi non valido');
								return;
							}

							const json = {
								id: parseInt($('#config_id').val()),
								group: $('#config_group').val() || null,
								identifier: $('#config_identifier').val(),
								type: $('#config_type').val(),
								label: $('#config_label').val(),
								enabled: $('#config_enabled').val() === 'true',
								translation_enabled: $('#config_translation_enabled').val() === 'true',
								required: $('#config_required').val() === 'true',
								crud_route: $('#config_crud_route').val() || null,
								css_classes: $('#config_css_classes').val() || null,
								placeholder: $('#config_placeholder').val() || null,
								info: $('#config_info').val() || null,
								order: parseInt($('#config_order').val() || '0'),
								attrs: attrs,
							};

							const fieldIdx = FormConfigs.findIndex((field) => field.id === json.id);
							if (fieldIdx === -1) {
								toastr.error('Configurazione campo non trovata');
								return;
							}

							$.ajax({
								type: 'POST',
								url: StringUtils.localizeUrl(window.appconfig.routes.form.edit + '?editing=true'),
								data: JSON.stringify(json),
								success: function (data) {
									location.reload();
								},
								error: function (jqXHR) {
									let errorMessage = 'Si è verificato un errore inatteso, contattare il dipartimento IT';
									try {
										const response = JSON.parse(jqXHR.responseText);
										errorMessage = response.message || errorMessage;
									} catch (e) {
										console.error('Errore nel parsing della risposta:', jqXHR.responseText);
									}
									toastr.error(errorMessage);
								},
								contentType: 'application/json',
								dataType: 'json',
							});
						});
					},
				},
				midClick: true,
			},
			0,
		);
	}

	static #getFormFieldHtml(fieldConfigs) {
		return `
			<h3 class="text-center">Configurazione campo form</h3>
			<form id="editor-configs-form" class="p-4">
				<input required readonly disabled type="hidden" name="config_id" id="config_id" value="${fieldConfigs.id}" />
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_page">Pagina</label>
					</h4>
					<div class="col-sm-9">
						<input required readonly disabled name="config_page" id="config_page" class="form-control" value="${fieldConfigs.page}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_group">Gruppo</label>
					</h4>
					<div class="col-sm-9">
						<input name="config_group" id="config_group" class="form-control" value="${fieldConfigs.group ?? ''}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_identifier">Campo</label>
					</h4>
					<div class="col-sm-9">
						<input required readonly disabled name="config_identifier" id="config_identifier" class="form-control" value="${fieldConfigs.identifier}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_type">Tipo</label>
					</h4>
					<div class="col-sm-9">
						<select required id="config_type" name="config_type" class="form-control">
							<option value="text" ${fieldConfigs.type === 'text' ? 'selected' : ''}>Text</option>
							<option value="select" ${fieldConfigs.type === 'select' ? 'selected' : ''}>Select</option>
							<option value="boolean" ${fieldConfigs.type === 'boolean' ? 'selected' : ''}>Boolean</option>
							<option value="switch" ${fieldConfigs.type === 'switch' ? 'selected' : ''}>Switch</option>
							<option value="checkbox" ${fieldConfigs.type === 'checkbox' ? 'selected' : ''}>Checkbox</option>
							<option value="password" ${fieldConfigs.type === 'password' ? 'selected' : ''}>Password</option>
							<option value="textarea" ${fieldConfigs.type === 'textarea' ? 'selected' : ''}>Textarea</option>
							<option value="date" ${fieldConfigs.type === 'date' ? 'selected' : ''}>Date</option>
							<option value="datetime" ${fieldConfigs.type === 'datetime' ? 'selected' : ''}>Datetime</option>
							<option value="editor" ${fieldConfigs.type === 'editor' ? 'selected' : ''}>Editor</option>
							<option value="url" ${fieldConfigs.type === 'url' ? 'selected' : ''}>Url</option>
							<option value="cover_advanced" ${fieldConfigs.type === 'cover_advanced' ? 'selected' : ''}>Cover Advanced</option>
						</select>
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_label">Etichetta</label>
					</h4>
					<div class="col-sm-9">
						<input required name="config_label" id="config_label" class="form-control" value="${fieldConfigs.label}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_enabled">Abilitato</label>
					</h4>
					<div class="col-sm-9">
						<select id="config_enabled" name="config_enabled" class="form-control">
							<option value="true" ${fieldConfigs.enabled ? 'selected' : ''}>S&igrave;</option>
							<option value="false" ${!fieldConfigs.enabled ? 'selected' : ''}>No</option>
						</select>
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_translation_enabled">Abilitato in traduzioni</label>
					</h4>
					<div class="col-sm-9">
						<select id="config_translation_enabled" name="config_translation_enabled" class="form-control">
							<option value="true" ${fieldConfigs.translation_enabled ? 'selected' : ''}>S&igrave;</option>
							<option value="false" ${!fieldConfigs.translation_enabled ? 'selected' : ''}>No</option>
						</select>
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_required">Obbligatorio</label>
					</h4>
					<div class="col-sm-9">
						<select id="config_required" name="config_required" class="form-control">
							<option value="true" ${fieldConfigs.required ? 'selected' : ''}>S&igrave;</option>
							<option value="false" ${!fieldConfigs.required ? 'selected' : ''}>No</option>
						</select>
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_crud_route">Abilita pulsanti Crud</label>
					</h4>
					<div class="col-sm-9">
						<input name="config_crud_route" placeholder="Inserire nome endpoint" ${
							fieldConfigs.type !== 'select' ? 'readonly disabled' : ''
						} id="config_crud_route" class="form-control" value="${fieldConfigs.crud_route ?? ''}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_css_classes">Classi css</label>
					</h4>
					<div class="col-sm-9">
						<input name="config_css_classes" id="config_css_classes" class="form-control" value="${fieldConfigs.css_classes ?? ''}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_placeholder">Placeholder</label>
					</h4>
					<div class="col-sm-9">
						<input name="config_placeholder" id="config_placeholder" class="form-control" value="${fieldConfigs.placeholder ?? ''}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_info">Tooltip</label>
					</h4>
					<div class="col-sm-9">
						<input name="config_info" id="config_info" class="form-control" value="${fieldConfigs.info ?? ''}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_order">Ordinamento</label>
					</h4>
					<div class="col-sm-9">
						<input required type="number" step="1" name="config_order" id="config_order" class="form-control" value="${fieldConfigs.order}" />
					</div>
				</div>
				<div class="form-group row">
					<h4 class="example-title col-sm-3 mb-0">
						<label for="config_attributes">Attributi html</label>
					</h4>
					<div class="col-sm-9">
						<textarea class="ace_text-input" autocorrect="off" autocapitalize="none" spellcheck="false" wrap="off"></textarea>
						<div id="config_attributes" style="width:100%;height: 100%;min-height:200px"></div>
					</div>
				</div>
				<button type="button" id="editor-configs-form-submit" class="btn btn-primary w-full">Salva</button>
			</form>
		`;
	}

	/**
	 * @param {JQuery} $field
	 * @returns {string}
	 */
	static getFieldErrorMessage($field) {
		const $zone = $field.closest('.err-zone');

		if (!$zone.length) {
			return '';
		}

		const $container = $zone.find('.err-container');
		const $invalid_message = $container.find('.help-block[data-fv-result="INVALID"]').first();

		return ($invalid_message.length ? $invalid_message : $container.find('.help-block').first()).text().trim();
	}

	/**
	 * jQuery `.wrap()`/`.wrapAll()` detach the element before re-inserting it, and a detached input
	 * loses focus and caret. The wrap is built lazily, so it would happen on the very first keystroke
	 * in the field being typed into — restore focus and selection around it.
	 *
	 * @param {Function} wrapper does the actual (re-inserting) DOM work
	 */
	static #preservingFocus(wrapper) {
		const active = document.activeElement;
		let selection = null;

		if (active && typeof active.setSelectionRange === 'function') {
			try {
				selection = { start: active.selectionStart, end: active.selectionEnd, direction: active.selectionDirection };
			} catch (error) {
				// Input types like email/number expose no selection: nothing to restore.
				selection = null;
			}
		}

		wrapper();

		if (!active || active === document.activeElement || !document.contains(active)) {
			return;
		}

		active.focus({ preventScroll: true });

		if (selection && selection.start !== null) {
			try {
				active.setSelectionRange(selection.start, selection.end, selection.direction || 'none');
			} catch (error) {
				// Selection could not be restored; the focus above is what matters.
			}
		}
	}

	/**
	 * @param {JQuery} $field
	 * @returns {JQuery}
	 */
	static ensureFieldControlWrap($field) {
		let $wrap = $field.closest('.field-control-wrap');

		if ($wrap.length) {
			return $wrap;
		}

		const $flex_one = $field.closest('.flex-one');

		if ($flex_one.length) {
			$flex_one.addClass('field-control-wrap position-relative');

			return $flex_one;
		}

		if ($field.hasClass('select2-hidden-accessible')) {
			const $select2_container = $field.next('.select2-container');

			if ($select2_container.length) {
				FormUtils.#preservingFocus(() => {
					$field.add($select2_container).wrapAll('<div class="field-control-wrap position-relative"></div>');
				});

				return $field.parent();
			}
		}

		FormUtils.#preservingFocus(() => {
			$field.wrap('<div class="field-control-wrap position-relative"></div>');
		});

		return $field.parent();
	}

	/**
	 * @param {JQuery} $wrap
	 * @returns {JQuery}
	 */
	static ensureInvalidTooltipTrigger($wrap) {
		let $trigger = $wrap.children('.field-invalid-tooltip-trigger');

		if (!$trigger.length) {
			$trigger = $(
				'<span class="field-invalid-tooltip-trigger" data-toggle="tooltip" data-placement="top" title="" aria-hidden="true"></span>',
			);
			$wrap.append($trigger);
		}

		return $trigger;
	}

	/**
	 * @param {JQuery} $field
	 * @param {boolean} show
	 */
	static syncInvalidFieldTooltip($field, show) {
		if (!$field?.length || !$field.closest('.err-zone').length) {
			return;
		}

		const message = show ? FormUtils.getFieldErrorMessage($field) : '';

		// Nothing to show must not build the wrap: every valid field would otherwise get wrapped —
		// and, the wrap being lazy, exactly while the user is typing in it, which drops the focus.
		if (!message) {
			const $existing_trigger = $field.closest('.field-control-wrap').children('.field-invalid-tooltip-trigger');

			if (!$existing_trigger.length) {
				return;
			}

			if ($existing_trigger.data('bs.tooltip')) {
				$existing_trigger.tooltip('dispose');
			}

			$existing_trigger.removeAttr('title data-original-title aria-label').hide();

			return;
		}

		const $wrap = FormUtils.ensureFieldControlWrap($field);
		const $trigger = FormUtils.ensureInvalidTooltipTrigger($wrap);

		$trigger.attr('title', message).attr('aria-label', message).show();

		if ($trigger.data('bs.tooltip')) {
			$trigger.tooltip('dispose');
		}

		$trigger.tooltip({ boundary: 'window', placement: 'top' });
	}

	static bindInvalidFieldTooltipHandlers() {
		if (FormUtils._invalid_tooltip_handlers_bound) {
			return;
		}

		FormUtils._invalid_tooltip_handlers_bound = true;

		$(document).on('err.field.fv', (_event, data) => {
			if (!data?.element) {
				return;
			}

			const $field = $(data.element);

			requestAnimationFrame(() => {
				FormUtils.syncInvalidFieldTooltip($field, true);
			});
		});

		$(document).on('success.field.fv', (_event, data) => {
			if (!data?.element) {
				return;
			}

			FormUtils.syncInvalidFieldTooltip($(data.element), false);
		});
	}
}
